import * as THREE from 'three';
import { CAMERA } from '../config/tuning';
import { clamp } from '../flight/Aerodynamics';
import { horizontalToVerticalFovDeg } from './Renderer';

/**
 * The nose camera (SPEC §3.3.1).
 *
 * A single perspective camera rigidly parented to the airframe, 4° down, 78° horizontal FOV.
 * **Not smoothed, not damped, not stabilised** — the horizon rotation *is* the roll indicator,
 * and any smoothing here would quietly remove the game's primary attitude cue.
 *
 * Two permitted deviations live here: a look-around cone that snaps back on release, and a zoom
 * that crops the FOV. Both are optional per difficulty.
 *
 * This class is also the HUD's projector. Every HUD element that has to sit somewhere in the
 * world — the flight path marker, bearing tags, the designation mark — goes through
 * `projectDirection`, so all of them share one definition of where the camera is looking.
 *
 * Units: metres, radians. Screen coordinates are CSS pixels with y down, origin top-left.
 */

const DEG_TO_RAD = Math.PI / 180;

export interface ScreenPoint {
  x: number;
  y: number;
  /** False when the point is behind the camera; x/y are then a bearing hint, not a position. */
  visible: boolean;
  /** Signed depth along the view axis, metres. Negative means behind. */
  depth: number;
}

export function createScreenPoint(): ScreenPoint {
  return { x: 0, y: 0, visible: false, depth: 0 };
}

/** A look-around command. Structurally satisfied by the input layer's LookCommand. */
export interface LookInput {
  rateX: number;
  rateY: number;
  deltaXRad: number;
  deltaYRad: number;
  active: boolean;
}

/** What the HUD needs from a camera. Implemented by NoseCamera and by the HUD test harness. */
export interface Projector {
  /** Project a unit direction in world space. Returns false when it is behind the camera. */
  projectDirection(worldDirection: THREE.Vector3, out: ScreenPoint): boolean;
  /** Project a world-space point. Returns false when it is behind the camera. */
  projectPoint(worldPoint: THREE.Vector3, out: ScreenPoint): boolean;
  /** Pixels per radian near the optical axis — the HUD's angular scale. */
  readonly pixelsPerRadian: number;
  readonly widthPx: number;
  readonly heightPx: number;
}

// Module-scope scratch: projection runs several times a frame and must not allocate.
const directionScratch = new THREE.Vector3();
const cameraSpaceScratch = new THREE.Vector3();
const inverseOrientationScratch = new THREE.Quaternion();
const tiltScratch = new THREE.Quaternion();
const lookScratch = new THREE.Quaternion();
const yawScratch = new THREE.Quaternion();
const pitchScratch = new THREE.Quaternion();

const RIGHT_AXIS = new THREE.Vector3(1, 0, 0);
const UP_AXIS = new THREE.Vector3(0, 1, 0);

export class NoseCamera implements Projector {
  readonly camera: THREE.PerspectiveCamera;

  /** Look-around offsets within the cone, radians. Positive yaw is right, positive pitch is up. */
  lookYawRad = 0;
  lookPitchRad = 0;

  /** Disabled on Realistic difficulty (SPEC §3.3.1). */
  lookAroundEnabled = true;

  /** 0 = full FOV, 1 = fully zoomed (cropped). */
  zoom = 0;

  private widthPxValue = 1;
  private heightPxValue = 1;
  private pixelsPerRadianValue = 1;
  private horizontalFovRad = CAMERA.fovH * DEG_TO_RAD;

  /** The camera's world orientation including tilt and look-around. */
  private readonly orientation = new THREE.Quaternion();

  constructor() {
    this.camera = new THREE.PerspectiveCamera(
      CAMERA.fovH,
      1,
      CAMERA.nearM,
      CAMERA.farM,
    );
    tiltScratch.setFromAxisAngle(RIGHT_AXIS, -CAMERA.tiltDownDeg * DEG_TO_RAD);
    this.resize(1, 1);
  }

  get widthPx(): number {
    return this.widthPxValue;
  }

  get heightPx(): number {
    return this.heightPxValue;
  }

  get pixelsPerRadian(): number {
    return this.pixelsPerRadianValue;
  }

  /** Current horizontal field of view, radians — narrowed by zoom. */
  get horizontalFov(): number {
    return this.horizontalFovRad;
  }

  /** True while the view is displaced from boresight. */
  get lookingAround(): boolean {
    return this.lookYawRad !== 0 || this.lookPitchRad !== 0;
  }

  /** Resize to a CSS pixel viewport. */
  resize(widthPx: number, heightPx: number): void {
    this.widthPxValue = Math.max(1, widthPx);
    this.heightPxValue = Math.max(1, heightPx);
    this.applyProjection();
  }

  /**
   * Advance the look-around and zoom states. When the look command is inactive the view returns
   * to boresight — the snap-back of SPEC §3.3.1.
   *
   * The parameter is declared structurally rather than imported from the input layer, so the
   * camera does not depend on how the player happens to be holding the controls.
   */
  updateView(dtS: number, look: LookInput, zoomHeld: boolean): void {
    if (this.lookAroundEnabled && look.active) {
      const maxYaw = CAMERA.lookConeYawDeg * DEG_TO_RAD * 0.5;
      const maxPitch = CAMERA.lookConePitchDeg * DEG_TO_RAD * 0.5;
      this.lookYawRad = clamp(
        this.lookYawRad + look.deltaXRad + look.rateX * CAMERA.lookPadRateRadPerS * dtS,
        -maxYaw,
        maxYaw,
      );
      this.lookPitchRad = clamp(
        this.lookPitchRad + look.deltaYRad + look.rateY * CAMERA.lookPadRateRadPerS * dtS,
        -maxPitch,
        maxPitch,
      );
    } else {
      const decay = Math.exp(-CAMERA.lookReturnPerS * dtS);
      this.lookYawRad *= decay;
      this.lookPitchRad *= decay;
      if (Math.abs(this.lookYawRad) < 1e-4) this.lookYawRad = 0;
      if (Math.abs(this.lookPitchRad) < 1e-4) this.lookPitchRad = 0;
    }

    const targetZoom = zoomHeld ? 1 : 0;
    const step = CAMERA.zoomRatePerS * dtS;
    this.zoom = targetZoom > this.zoom
      ? Math.min(targetZoom, this.zoom + step)
      : Math.max(targetZoom, this.zoom - step);
    this.applyProjection();
  }

  /**
   * Place the camera on the airframe. `positionM` and `orientation` are the *interpolated*
   * transform for this frame, not the last tick's (SPEC §4.3).
   */
  attachTo(positionM: THREE.Vector3, airframeOrientation: THREE.Quaternion): void {
    this.orientation.copy(airframeOrientation).multiply(tiltScratch);

    if (this.lookYawRad !== 0 || this.lookPitchRad !== 0) {
      yawScratch.setFromAxisAngle(UP_AXIS, -this.lookYawRad);
      pitchScratch.setFromAxisAngle(RIGHT_AXIS, this.lookPitchRad);
      lookScratch.copy(yawScratch).multiply(pitchScratch);
      this.orientation.multiply(lookScratch);
    }

    this.camera.position.copy(positionM);
    this.camera.quaternion.copy(this.orientation);
  }

  /** Direct orientation set, for the HUD test scene. */
  setOrientation(orientation: THREE.Quaternion): void {
    this.orientation.copy(orientation).multiply(tiltScratch);
    this.camera.quaternion.copy(this.orientation);
  }

  projectPoint(worldPoint: THREE.Vector3, out: ScreenPoint): boolean {
    directionScratch.copy(worldPoint).sub(this.camera.position);
    return this.projectDirection(directionScratch, out);
  }

  /**
   * Project a direction through the camera. Deliberately does the perspective divide by hand
   * rather than going through `Vector3.project`, because a point behind the camera must produce
   * a usable *bearing* for the HUD's edge carets rather than a mirrored screen position.
   */
  projectDirection(worldDirection: THREE.Vector3, out: ScreenPoint): boolean {
    inverseOrientationScratch.copy(this.orientation).invert();
    cameraSpaceScratch.copy(worldDirection).applyQuaternion(inverseOrientationScratch);

    // Camera looks down −Z, so forward distance is −z.
    const forward = -cameraSpaceScratch.z;
    out.depth = forward;

    const focalPx = this.pixelsPerRadianValue;
    if (forward <= 1e-6) {
      // Behind the camera: report the direction it lies in, flagged invisible, so the caller can
      // clamp a caret to the correct screen edge instead of drawing a mirrored ghost.
      out.visible = false;
      const magnitude = Math.hypot(cameraSpaceScratch.x, cameraSpaceScratch.y) || 1;
      out.x = this.widthPxValue * 0.5 + (cameraSpaceScratch.x / magnitude) * this.widthPxValue;
      out.y = this.heightPxValue * 0.5 - (cameraSpaceScratch.y / magnitude) * this.heightPxValue;
      return false;
    }

    out.x = this.widthPxValue * 0.5 + (cameraSpaceScratch.x / forward) * focalPx;
    out.y = this.heightPxValue * 0.5 - (cameraSpaceScratch.y / forward) * focalPx;
    out.visible = true;
    return true;
  }

  /** Vertical FOV, radians, for the current aspect and zoom. */
  get verticalFov(): number {
    return this.camera.fov * DEG_TO_RAD;
  }

  private applyProjection(): void {
    const fovHDeg = CAMERA.fovH + (CAMERA.zoomFovH - CAMERA.fovH) * this.zoom;
    this.horizontalFovRad = fovHDeg * DEG_TO_RAD;

    const aspect = this.widthPxValue / this.heightPxValue;
    const fovVDeg = horizontalToVerticalFovDeg(fovHDeg, aspect);
    const fovVRad = fovVDeg * DEG_TO_RAD;

    this.camera.aspect = aspect;
    this.camera.fov = fovVDeg;
    this.camera.updateProjectionMatrix();

    // Focal length in pixels. Also the small-angle pixels-per-radian the HUD scales by.
    this.pixelsPerRadianValue = this.heightPxValue * 0.5 / Math.tan(fovVRad * 0.5);
  }
}
